import { Alert, Button, InputNumber, Space, Switch, Table, Typography, message } from 'antd'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { platformListPlanCatalog, platformListRateRules, platformUpdateRateRule } from '../api'
import type { PlanCatalogRow, RateRuleRow } from '../types'
import { SectionCard, formatBytes, formatDateTime } from './adminShared'

/**
 * Stage S4 — ADMIN_RATE_EDIT + read-only Plan Catalog view. Rate Rule
 * edits go through `PATCH /platform/rate-rules/{rule_key}` (Stage S2's
 * `rate_rule_service.update_rate`/`set_enabled`, now exposed over HTTP);
 * every save writes an `AdminAuditLog` row server-side (see
 * `saas_platform.py::update_rate_rule`). Editing rate rules only ever
 * changes the LIVE rate going forward — it never touches any
 * `CreditLedgerEntry.rate_snapshot` already written against the old rate
 * (Stage S2's `HISTORICAL_RATE_STABILITY` guarantee), which is why the
 * warning text below is shown next to the editable table.
 */
export default function RateRulesPage() {
  const [rules, setRules] = useState<RateRuleRow[]>([])
  const [plans, setPlans] = useState<PlanCatalogRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [draftRates, setDraftRates] = useState<Record<string, number>>({})
  const [saving, setSaving] = useState<Record<string, boolean>>({})

  const load = () => {
    setLoading(true)
    setError(null)
    Promise.all([platformListRateRules(), platformListPlanCatalog()])
      .then(([r, p]) => {
        setRules(r.data)
        setPlans(p.data)
      })
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const saveRate = async (ruleKey: string) => {
    const draft = draftRates[ruleKey]
    if (draft === undefined) return
    setSaving((s) => ({ ...s, [ruleKey]: true }))
    try {
      const res = await platformUpdateRateRule(ruleKey, { rate: draft })
      setRules((prev) => prev.map((r) => (r.rule_key === ruleKey ? res.data : r)))
      setDraftRates((d) => {
        const next = { ...d }
        delete next[ruleKey]
        return next
      })
      message.success('费率已更新')
    } catch (err) {
      message.error((err as Error).message)
    } finally {
      setSaving((s) => ({ ...s, [ruleKey]: false }))
    }
  }

  const toggleEnabled = async (ruleKey: string, enabled: boolean) => {
    try {
      const res = await platformUpdateRateRule(ruleKey, { enabled })
      setRules((prev) => prev.map((r) => (r.rule_key === ruleKey ? res.data : r)))
    } catch (err) {
      message.error((err as Error).message)
    }
  }

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          费率与套餐
        </Typography.Title>
        <Typography.Text type="secondary">管理计费费率规则与套餐目录。</Typography.Text>
      </div>

      {error && <Alert type="error" message={error} showIcon style={{ margin: '12px 0' }} />}

      <SectionCard title="费率规则（Rate Rules）">
        <Alert
          type="info"
          showIcon
          style={{ margin: '4px 4px 8px' }}
          message="修改仅影响未来产生的计费，不会追溯影响历史积分流水（rate_snapshot 已冻结）。"
        />
        <Table
          className="pf-data-table"
          rowKey="rule_key"
          loading={loading}
          dataSource={rules}
          columns={[
            { title: '名称', dataIndex: 'display_name' },
            { title: '规则 Key', dataIndex: 'rule_key' },
            { title: '单位', dataIndex: 'unit' },
            {
              title: '当前费率',
              dataIndex: 'rate',
              render: (v: string, row) => (
                <Space>
                  <InputNumber
                    size="small"
                    min={0}
                    step={0.1}
                    value={draftRates[row.rule_key] ?? Number(v)}
                    onChange={(val) => setDraftRates((d) => ({ ...d, [row.rule_key]: val ?? 0 }))}
                    style={{ width: 100 }}
                  />
                  <Button
                    size="small"
                    type="primary"
                    loading={saving[row.rule_key]}
                    disabled={draftRates[row.rule_key] === undefined || draftRates[row.rule_key] === Number(v)}
                    onClick={() => saveRate(row.rule_key)}
                  >
                    保存
                  </Button>
                </Space>
              ),
            },
            {
              title: '启用',
              dataIndex: 'enabled',
              render: (v: boolean, row) => <Switch checked={v} onChange={(checked) => toggleEnabled(row.rule_key, checked)} />,
            },
            { title: '更新时间', dataIndex: 'updated_at', render: (v: string) => formatDateTime(v) },
            { title: '更新人', dataIndex: 'updated_by', render: (v: string | null) => v ?? '—' },
          ]}
        />
      </SectionCard>

      <SectionCard title="套餐目录（Plan Catalog，只读）">
        <Alert
          type="info"
          showIcon
          style={{ margin: '4px 4px 8px' }}
          message={
            <span>
              VISTOERA_BILLING_PLAN_CREDITS_TOPUP_PAYG_REWARDS_AND_PROMOTIONS_FOUNDATION_P0_V1 —
              套餐（Plan）的创建/编辑已迁移到新的
              <Link to="/platform/plan-credits?tab=plans"> 「套餐与积分」</Link>
              页面（含多币种定价、基础/赠送积分拆分、耗尽策略等）。下表保留作为历史只读视图，不再是套餐编辑入口。
            </span>
          }
        />
        <Table
          className="pf-data-table"
          rowKey="key"
          loading={loading}
          dataSource={plans}
          pagination={false}
          columns={[
            { title: '套餐', dataIndex: 'display_name' },
            { title: 'Key', dataIndex: 'key' },
            { title: '基础存储', dataIndex: 'base_storage_bytes', render: (v: number) => formatBytes(v) },
            { title: '赠送积分', dataIndex: 'credit_grant', align: 'right' as const },
            {
              title: '注册默认',
              dataIndex: 'is_registration_default',
              render: (v: boolean) => (v ? '是' : '否'),
            },
          ]}
        />
      </SectionCard>
    </div>
  )
}
